/**
 * DataPill Component
 *
 * A reusable monochrome pill component for displaying data counts.
 * Used for completion states, summaries, and anywhere count + label pairs
 * need to be displayed in a compact, visually consistent format.
 *
 * Design:
 * - Light gray background (Polaris surface-secondary)
 * - Subdued text color for labels
 * - Emphasized count value
 * - Compact pill shape with consistent padding
 * - Optional icon for visual context
 */

import { Text, Icon } from '@shopify/polaris';
import type { IconSource } from '@shopify/polaris';

export interface DataPillProps {
  /** The numeric or string value to display prominently */
  value: number | string;
  /** Label describing the value (e.g., "products", "alerts") */
  label: string;
  /** Optional icon to display before the value */
  icon?: IconSource;
  /** Optional additional CSS class */
  className?: string;
}

/**
 * Renders a monochrome pill with a value and label.
 *
 * @example
 * <DataPill value={42} label="products" />
 * // Renders: [42 products] in a light gray pill
 *
 * @example
 * <DataPill value="150" label="velocity records" />
 * // Renders: [150 velocity records] in a light gray pill
 *
 * @example
 * <DataPill value={3} label="variants" icon={ProductIcon} />
 * // Renders: [icon 3 variants] in a light gray pill with icon
 */
export function DataPill({ value, label, icon, className = '' }: DataPillProps) {
  return (
    <span className={`data-pill ${className}`.trim()}>
      {icon && (
        <span className="data-pill-icon">
          <Icon source={icon} tone="subdued" />
        </span>
      )}
      <Text as="span" variant="bodySm" fontWeight="semibold">
        {value}
      </Text>
      <Text as="span" variant="bodySm" tone="subdued">
        {' '}{label}
      </Text>
    </span>
  );
}
